/**
 * finance-core — Nợ & lịch trả nợ (amortization)
 *
 * Module thuần hàm, nguồn chân lý duy nhất cho toán trả nợ.
 * Lãi suất truyền vào là %/năm, tính lãi đơn trên dư nợ giảm dần theo tháng.
 */

export interface DebtPayoffResult {
  /** Số tháng đến khi trả hết; Infinity nếu khoản trả không đủ bù lãi */
  months: number;
  years: number;
  totalPaid: number;
  totalInterest: number;
  /** Cảnh báo khi không bao giờ trả hết */
  warning?: string;
}

export interface AmortizationRow {
  month: number;
  interestPaid: number;
  principalPaid: number;
  payment: number;
  remainingBalance: number;
}

const MAX_MONTHS = 50 * 12; // chặn trần 50 năm

/**
 * Tổng kết thời gian & chi phí trả hết một khoản nợ với khoản trả cố định hàng tháng.
 * Tháng cuối chỉ trả phần còn thiếu (không trả lố), nên totalPaid phản ánh đúng
 * số tiền thực chi thay vì làm tròn theo cả khoản trả cố định.
 */
export const debtPayoff = (
  principal: number,
  annualRatePercent: number,
  monthlyPayment: number
): DebtPayoffResult => {
  if (principal <= 0) {
    return { months: 0, years: 0, totalPaid: 0, totalInterest: 0 };
  }
  if (monthlyPayment <= 0) {
    return {
      months: Infinity,
      years: Infinity,
      totalPaid: Infinity,
      totalInterest: Infinity,
      warning: 'Chưa có khoản trả hàng tháng - Không thể trả hết nợ'
    };
  }

  const r = annualRatePercent / 12 / 100;
  let balance = principal;
  let months = 0;
  let totalPaid = 0;

  while (balance > 0 && months < MAX_MONTHS) {
    const interest = balance * r;

    if (monthlyPayment <= interest) {
      return {
        months: Infinity,
        years: Infinity,
        totalPaid: Infinity,
        totalInterest: Infinity,
        warning: 'Thanh toán hàng tháng không đủ để trả lãi - Không thể trả hết nợ'
      };
    }

    const principalPayment = Math.min(monthlyPayment - interest, balance);
    balance -= principalPayment;
    totalPaid += principalPayment + interest;
    months++;
  }

  return {
    months,
    years: months / 12,
    totalPaid,
    totalInterest: totalPaid - principal
  };
};

/**
 * Lịch trả nợ chi tiết theo từng tháng (dùng cho bảng amortization).
 * Trả về mảng rỗng nếu khoản trả không đủ bù lãi.
 */
export const amortizationSchedule = (
  principal: number,
  annualRatePercent: number,
  monthlyPayment: number
): AmortizationRow[] => {
  if (principal <= 0 || monthlyPayment <= 0) return [];

  const r = annualRatePercent / 12 / 100;
  const rows: AmortizationRow[] = [];
  let balance = principal;
  let month = 0;

  while (balance > 0 && month < MAX_MONTHS) {
    const interest = balance * r;
    if (monthlyPayment <= interest) return [];

    const principalPaid = Math.min(monthlyPayment - interest, balance);
    balance -= principalPaid;
    month++;

    rows.push({
      month,
      interestPaid: interest,
      principalPaid,
      payment: principalPaid + interest,
      remainingBalance: balance
    });
  }

  return rows;
};

/**
 * Khoản trả cố định hàng tháng để trả hết nợ trong đúng n tháng (công thức PMT chuẩn).
 * PMT = P·r / (1 − (1+r)^−n)
 */
export const fixedMonthlyPayment = (
  principal: number,
  annualRatePercent: number,
  months: number
): number => {
  if (principal <= 0 || months <= 0) return 0;
  const r = annualRatePercent / 12 / 100;
  if (r === 0) return principal / months;
  return (principal * r) / (1 - Math.pow(1 + r, -months));
};

/** Chênh lệch làm tròn tối đa (đ) ở kỳ cuối mà ta tự chốt dư nợ về 0 thay vì báo lỗi. */
export const DEBT_ROUNDING_TOLERANCE = 1000;

export interface DebtSplit {
  interest: number; principal: number;
  /** Gốc vượt dư nợ còn lại sau khi đã bù sai số làm tròn (0 = hợp lệ) */
  excess: number;
}

/**
 * Tách một khoản trả nợ thành lãi + gốc. Lãi mặc định = dư nợ × lãi suất năm ÷ 12 (dư nợ giảm dần, đúng cách
 * ngân hàng tính bảng trả góp); có thể truyền lãi thực tế theo sao kê. Gốc vượt dư nợ chỉ vài đồng (làm tròn ở
 * kỳ cuối) thì phần dư chuyển sang lãi để dư nợ về đúng 0.
 */
export const splitDebtPayment = (
  remaining: number, annualRatePercent: number, amount: number, interestOverride?: number | null
): DebtSplit => {
  const auto = Math.min(amount, Math.max(0, Math.round(remaining * annualRatePercent / 100 / 12)));
  let interest = interestOverride == null ? auto : Math.min(amount, Math.max(0, Math.round(interestOverride)));
  let principal = amount - interest;
  if (principal > remaining && principal - remaining <= DEBT_ROUNDING_TOLERANCE) { principal = remaining; interest = amount - remaining; }
  return { interest, principal, excess: Math.max(0, principal - remaining) };
};

/** Đã nhập lãi suất chưa? 0 là lãi suất hợp lệ (vay không lãi); chỉ thiếu khi chưa có số (null/để trống). */
export const hasInterestRate = (v: unknown): boolean => v !== null && v !== undefined && v !== '' && Number.isFinite(Number(v));

export interface LoanCorrection { lender: string; principal: number; remainingBalance: number; interestRate?: number; term?: number; monthlyPayment?: number | null }
/** Kiểm tra bản sửa thông tin khoản vay (nhập nhầm số): trả thông điệp lỗi hoặc null nếu hợp lệ. */
export const validateLoanCorrection = (c: LoanCorrection): string | null => {
  if (!c.lender?.trim()) return 'Nhập tên bên cho vay';
  if (!(Number.isFinite(c.principal) && c.principal > 0)) return 'Tiền vay gốc phải lớn hơn 0';
  if (!(Number.isFinite(c.remainingBalance) && c.remainingBalance >= 0)) return 'Dư nợ còn lại không hợp lệ';
  if (c.remainingBalance > c.principal) return 'Dư nợ còn lại không thể lớn hơn tiền vay gốc';
  if (c.interestRate !== undefined && !(Number.isFinite(c.interestRate) && c.interestRate >= 0 && c.interestRate <= 100)) return 'Lãi suất từ 0 đến 100 %/năm (nhập 0 nếu vay không lãi)';
  if (c.term !== undefined && !(Number.isFinite(c.term) && c.term >= 0)) return 'Số tháng không hợp lệ';
  if (c.monthlyPayment != null && !(Number.isFinite(c.monthlyPayment) && c.monthlyPayment >= 0)) return 'Số trả mỗi tháng không hợp lệ';
  return null;
};

/** Trả GỐC khoản vay gắn thẻ thì dư nợ thẻ giảm theo (không âm). */
export const cardUsedAfterLoanPayment = (used: number, principalPaid: number): number => Math.max(0, (Number(used) || 0) - (Number(principalPaid) || 0));
/** Hoàn tác trả gốc: dư nợ thẻ tăng lại, không vượt hạn mức (khi hạn mức đã đặt). */
export const cardUsedAfterLoanReversal = (used: number, principalPaid: number, limit?: number): number => {
  const next = (Number(used) || 0) + (Number(principalPaid) || 0);
  return Number(limit) > 0 ? Math.min(next, Number(limit)) : next;
};
